import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight } from 'lucide-react';
import InternalLinkCTA from './InternalLinkCTA';
import RelatedPostsPanel from './RelatedPostsPanel';
import FreeGuideCTA from './FreeGuideCTA';

interface BlogPostProps {
  id: string;
  title: string;
  content: string;
  author: string;
  publishedAt: string;
  readTime: string;
  category: string;
  image?: string;
  tags?: string[];
  niche?: string;
  pillarSlug?: string;
  pillarLabel?: string;
  siblingPosts?: Array<{ slug: string; title: string; excerpt: string }>;
}

const BlogPost: React.FC<BlogPostProps> = ({
  id,
  title,
  content,
  author,
  publishedAt,
  readTime,
  category,
  image,
  tags = [],
  niche,
  pillarSlug,
  pillarLabel,
  siblingPosts = [],
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <article className="max-w-4xl mx-auto">
      {/* Back to Blog */}
      <div className="mb-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-accent-600 hover:text-accent-700 font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>
      </div>

      {/* Article Header */}
      <header className="mb-8">
        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
          <span className="bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">
            {category}
          </span>
          <div className="flex items-center space-x-1">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(publishedAt)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>{readTime}</span>
          </div>
        </div>
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-4">
          {title}
        </h1>
        
        <div className="text-gray-600">
          By {author}
        </div>
      </header>

      {/* Featured Image */}
      {image && (
        <div className="mb-8">
          <img
            src={image}
            alt={title}
            className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
            loading="lazy"
          />
        </div>
      )}

      {/* Article Content */}
      <div className="prose prose-lg max-w-none">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Internal Link CTA */}
      <InternalLinkCTA niche={niche} />

      {/* Related Posts Panel */}
      <RelatedPostsPanel
        pillarSlug={pillarSlug}
        pillarLabel={pillarLabel}
        relatedPosts={siblingPosts}
      />

      {/* Free Guide Lead Magnet */}
      <div className="mt-12">
        <FreeGuideCTA location={`Blog Post: ${id}`} variant="card" />
      </div>

      {/* CTA Section */}
      <div className="mt-12 bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 text-center">
        <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
          Ready to Start Your Healing Journey?
        </h3>
        <p className="text-gray-600 mb-6">
          If this article resonated with you, consider booking a free consultation to discuss how hypnotherapy can help you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center justify-center"
          >
            Book Free Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
          <a
            href="tel:07932139245"
            className="btn-secondary inline-flex items-center justify-center"
          >
            Call 07932 139245
          </a>
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
